package one.devsbox.www.ui.widgets

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.onPointerEvent
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.ClockOutline
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.isoDayNumber
import one.devsbox.commons.nowDateTime
import one.devsbox.commons.timeOffset
import one.devsbox.shared.msg.MessageBody
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt
import kotlin.time.Clock


@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun OpeningHours(
    modifier: Modifier = Modifier,
    openingHours: List<MessageBody.Config.OpeningHour>,
    backgroundColor: Color,
    primaryColor: Color,
    showTitle: Boolean = true
) {

    val oh = remember {
        MessageBody.Config.OpeningHour.fromUTC(openingHours, Clock.timeOffset())
    }
    Column(modifier) {
        if (showTitle) {
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                Icon(
                    imageVector = Svg.ClockOutline,
                    contentDescription = null,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                Text(
                    text = "OPENING HOURS",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
            Spacer(Modifier.height(16.dp))
        }


        val textMeasurer = rememberTextMeasurer()
        val onSurfaceVariant = MaterialTheme.colorScheme.onSurfaceVariant
        val labelStyle = MaterialTheme.typography.labelSmall.copy(fontSize = 9.sp, color = onSurfaceVariant)
        val hPadding = 20.dp

        Canvas(modifier = Modifier.fillMaxWidth().padding(start = 32.dp)) {
            val totalMinutes = 24 * 60
            val hPaddingPx = hPadding.toPx()
            val usableWidth = size.width - 2 * hPaddingPx

            val times = listOf(
                "00:00" to 0,
                "04:00" to 4 * 60,
                "08:00" to 8 * 60,
                "12:00" to 12 * 60,
                "16:00" to 16 * 60,
                "20:00" to 20 * 60,
                "24:00" to 24 * 60
            )

            times.forEach { (time, minutes) ->
                val x = hPaddingPx + (minutes.toFloat() / totalMinutes) * usableWidth

                val textLayoutResult = textMeasurer.measure(time, labelStyle)
                drawText(
                    textLayoutResult = textLayoutResult,
                    topLeft = Offset(
                        x - textLayoutResult.size.width / 2,
                        0f
                    )
                )
            }
        }

        Spacer(Modifier.height(8.dp))

        var hoveredDay by remember { mutableStateOf<Int?>(null) }
        var mousePosition by remember { mutableStateOf(Offset.Zero) }

        BoxWithConstraints(
            Modifier
                .fillMaxWidth()
                .onPointerEvent(PointerEventType.Move) { event ->
                    mousePosition = event.changes.firstOrNull()?.position ?: Offset.Zero
                }
                .onPointerEvent(PointerEventType.Exit) {
                    hoveredDay = null
                }
        ) {
            val tableWidthPx = constraints.maxWidth.toFloat()

            // Global Vertical Grid Lines Canvas
            Canvas(modifier = Modifier.matchParentSize().padding(start = 32.dp)) {
                val totalMinutes = 24 * 60
                val hPaddingPx = hPadding.toPx()
                val usableWidth = size.width - 2 * hPaddingPx
                val gridTimes = listOf(0, 4 * 60, 8 * 60, 12 * 60, 16 * 60, 20 * 60, 24 * 60)

                gridTimes.forEach { minutes ->
                    val x = hPaddingPx + (minutes.toFloat() / totalMinutes) * usableWidth
                    drawLine(
                        color = onSurfaceVariant.copy(alpha = 0.1f),
                        start = Offset(x, 0f),
                        end = Offset(x, size.height),
                        strokeWidth = 1.dp.toPx()
                    )
                }
            }

            Column(Modifier.fillMaxWidth()) {
                (1..7).forEach { day ->
                    val hours = oh.filter { it.dow == day }

                    Row(
                        Modifier
                            .fillMaxWidth()
                            .padding(vertical = 4.dp)
                            .onPointerEvent(PointerEventType.Enter) {
                                hoveredDay = day
                            }
                            .onPointerEvent(PointerEventType.Exit) {
                                if (hoveredDay == day) {
                                    hoveredDay = null
                                }
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = stringResource(dowToString(day)),
                            style = LocalTextStyle.current.copy(
                                fontFamily = FontFamily.Monospace,
                                fontSize = 11.sp,
                                letterSpacing = 0.sp
                            ),
                            modifier = Modifier.width(32.dp)
                        )

                        Canvas(modifier = Modifier.height(12.dp).weight(1f)) {
                            val totalMinutes = 24 * 60
                            val hPaddingPx = hPadding.toPx()
                            val usableWidth = size.width - 2 * hPaddingPx

                            // Background line
                            drawRect(
                                color = backgroundColor,
                                topLeft = Offset(hPaddingPx, 0f),
                                size = Size(usableWidth, size.height)
                            )

                            // Active sequences
                            hours.forEach { oh ->
                                val startMinutes = oh.from.hour * 60 + oh.from.minute
                                val endMinutes = oh.to.hour * 60 + oh.to.minute

                                val startX = hPaddingPx + (startMinutes.toFloat() / totalMinutes) * usableWidth
                                val endX = hPaddingPx + (endMinutes.toFloat() / totalMinutes) * usableWidth

                                drawRect(
                                    color = primaryColor,
                                    topLeft = Offset(startX, 0f),
                                    size = Size(endX - startX, size.height)
                                )
                            }
                        }
                    }
                }
            }

            val hoveredHours = oh.filter { it.dow == hoveredDay }
            val currentDay = hoveredDay

            if (currentDay != null) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .offset {
                            val x = if (mousePosition.x > tableWidthPx / 2) {
                                mousePosition.x - 130.dp.toPx()
                            } else {
                                mousePosition.x + 16.dp.toPx()
                            }
                            val safeX = x.coerceAtLeast(0f)

                            val y = if (currentDay >= 5) {
                                mousePosition.y - 85.dp.toPx()
                            } else {
                                mousePosition.y + 16.dp.toPx()
                            }
                            val safeY = y.coerceAtLeast(0f)

                            IntOffset(
                                safeX.roundToInt(),
                                safeY.roundToInt()
                            )
                        }
                        .shadow(8.dp, RoundedCornerShape(4.dp))
                        .background(
                            color = MaterialTheme.colorScheme.surface.copy(alpha = 0.95f),
                            shape = RoundedCornerShape(4.dp)
                        )
                        .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(4.dp))
                        .padding(8.dp)
                        .width(110.dp)
                ) {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = stringResource(dowToStringLong(currentDay)),
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary
                        )
                        Spacer(
                            modifier = Modifier
                                .height(1.dp)
                                .fillMaxWidth()
                                .background(MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
                        )
                        if (hoveredHours.isEmpty()) {
                            Text(
                                text = "Closed",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        } else {
                            hoveredHours.forEach { oh ->
                                val fromStr = "${oh.from.hour.toString().padStart(2, '0')}:${
                                    oh.from.minute.toString().padStart(2, '0')
                                }"
                                val toStr = "${oh.to.hour.toString().padStart(2, '0')}:${
                                    oh.to.minute.toString().padStart(2, '0')
                                }"
                                Text(
                                    text = "$fromStr - $toStr",
                                    style = MaterialTheme.typography.bodySmall,
                                    fontFamily = FontFamily.Monospace,
                                    fontWeight = FontWeight.Medium,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

fun dowToString(dow: Int): StringResource = when (dow) {
    DayOfWeek.MONDAY.isoDayNumber -> Res.string.dow_mon
    DayOfWeek.TUESDAY.isoDayNumber -> Res.string.dow_tue
    DayOfWeek.WEDNESDAY.isoDayNumber -> Res.string.dow_wed
    DayOfWeek.THURSDAY.isoDayNumber -> Res.string.dow_thu
    DayOfWeek.FRIDAY.isoDayNumber -> Res.string.dow_fri
    DayOfWeek.SATURDAY.isoDayNumber -> Res.string.dow_sat
    DayOfWeek.SUNDAY.isoDayNumber -> Res.string.dow_sun
    else -> Res.string.empty
}

fun dowToStringLong(dow: Int): StringResource = when (dow) {
    DayOfWeek.MONDAY.isoDayNumber -> Res.string.dow_monday
    DayOfWeek.TUESDAY.isoDayNumber -> Res.string.dow_tuesday
    DayOfWeek.WEDNESDAY.isoDayNumber -> Res.string.dow_wednesday
    DayOfWeek.THURSDAY.isoDayNumber -> Res.string.dow_thursday
    DayOfWeek.FRIDAY.isoDayNumber -> Res.string.dow_friday
    DayOfWeek.SATURDAY.isoDayNumber -> Res.string.dow_saturday
    DayOfWeek.SUNDAY.isoDayNumber -> Res.string.dow_sunday
    else -> Res.string.empty
}